//
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarSeparator,
} from "@/components/ui/sidebar";
import { Home, CalendarDays, Images } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import logo_bg from "@/asset/img/logo/logo_bg.png";
import michaelPfp from "@/asset/img/team/michael.png";
import paddyPfp from "@/asset/img/team/paddy.png";
import tanyaPfp from "@/asset/img/team/tanya.png";

type EventItem = {
	title: string;
	date: string;
	link: string;
};

type TeamMember = {
	name: string;
	link: string;
	img: string;
};

const teamMembers: TeamMember[] = [
	{ name: "Michael", link: "https://t.me/revofire", img: michaelPfp },
	{ name: "Paddy", link: "https://t.me/bricsHQ", img: paddyPfp },
	{ name: "Tanya", link: "https://www.instagram.com/oh_bebegirl/", img: tanyaPfp },
];

const upcomingEvents: EventItem[] = [
	{
		title: "sui games showcase @ taipei 101 [tbw2025]",
		date: "Sep. 6, 2025",
		link: "https://lu.ma/xsjsqn5w",
	},
];

const events: EventItem[] = [
	{
		title: "gaming hackathon ft. mexc, iobeacon, piccollage, polygon, vircadia",
		date: "Jun. 8 - 22, 2025",
		link: "https://drive.google.com/drive/folders/1zfKCbADUQP4SCS-oaw145jv1Lq6BEFwI?usp=drive_link",
	},
	{
		title: "polygon demo day",
		date: "Apr. 6, 2025",
		link: "https://drive.google.com/drive/folders/1bQJUPyukVnnNCGEH9wAI0WTJUJid3Iag?usp=sharing",
	},
	{
		title: "hack social",
		date: "Mar. 27, 2025",
		link: "https://drive.google.com/drive/folders/1LcROn7a55hKHLgNeKyyb8mPNc__y4Ie1?usp=sharing",
	},
	{
		title: "ai & xr social",
		date: "Apr. 15, 2023",
		link: "https://www.instagram.com/p/CrD4laHrQBe/?img_index=1",
	},
	{
		title: "ai & xr social",
		date: "Mar. 18, 2023",
		link: "https://www.instagram.com/p/CqGm3MGvstt/?img_index=1",
	},
	{
		title: "ai & xr social",
		date: "Feb. 10, 2023",
		link: "https://www.instagram.com/p/CojLDsrpyqo/?img_index=1",
	},
];

export function AppSidebar() {
	return (
		<Sidebar collapsible="offcanvas">
			<SidebarHeader>
				<div className="flex items-center gap-2 p-2">
					<img src={logo_bg} alt="ED3N" className="h-16 w-16 rounded-sm" />
					<div className="flex flex-col leading-tight">
						<span className="text-sm font-medium">ED3N</span>
						<span className="text-xs text-muted-foreground">
							connect with influence
						</span>
					</div>
				</div>
				<SidebarSeparator />
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton asChild tooltip="App">
									<a href="/">
										<Home />
										<span>App</span>
									</a>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				<SidebarGroup>
					<SidebarGroupLabel>Upcoming Events</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{upcomingEvents.map((evt) => (
								<SidebarMenuItem key={`${evt.title}-${evt.date}`}>
									<SidebarMenuButton
										asChild
										tooltip={evt.date}
										size="lg"
										className="h-auto items-start"
									>
										<a
											href={evt.link}
											target="_blank"
											rel="noreferrer noopener"
											className="items-start"
										>
											<CalendarDays />
											<div className="flex min-w-0 flex-col leading-tight whitespace-normal">
												<span className="font-medium">{evt.title}</span>
												<span className="text-xs text-muted-foreground">
													{evt.date}
												</span>
											</div>
										</a>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				<SidebarGroup>
					<SidebarGroupLabel>Past Events</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{events.map((evt) => (
								<SidebarMenuItem key={`${evt.title}-${evt.date}`}>
									<SidebarMenuButton
										asChild
										tooltip={evt.date}
										size="lg"
										className="h-auto items-start"
									>
										<a
											href={evt.link}
											target="_blank"
											rel="noreferrer noopener"
											className="items-start"
										>
											<Images />
											<div className="flex min-w-0 flex-col leading-tight whitespace-normal">
												<span className="font-medium">{evt.title}</span>
												<span className="text-xs text-muted-foreground">
													{evt.date}
												</span>
											</div>
										</a>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				<SidebarGroup>
					<SidebarGroupLabel>Team</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{teamMembers.map((member) => (
								<SidebarMenuItem key={member.name}>
									<SidebarMenuButton
										asChild
										size="lg"
										tooltip={member.name}
										className="h-auto items-center"
									>
										<a
											href={member.link}
											target="_blank"
											rel="noreferrer noopener"
										>
											<Avatar className="size-8">
												<AvatarImage src={member.img} alt={member.name} />
												<AvatarFallback>
													{member.name.slice(0, 2).toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<span className="font-medium group-data-[collapsible=icon]:hidden">
												{member.name}
											</span>
										</a>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter />
		</Sidebar>
	);
}

export default AppSidebar;
